import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router-dom';

import { updateCustomer } from 'api/customers';
import { getAllTechnicians } from 'api/technicians';
import { getAllDoctors } from 'api/doctors';
import {
  getPolysomnographyById,
  updatePolysomnography,
} from 'api/polysomnographies';

import { UserContext } from 'contexts/user';
import {
  NEXT_ACTIONS,
  MANUAL_PAYMENT_STATUSES,
  RECORDING_STATUSES,
  STATUSES,
} from 'config/polysomnography';
import { BLANK } from 'config/shared';

import { sanitizePolysomnography } from 'helpers/general';

import ViewComponent from './view_component';

const PolysomnographiesEdit = ({
  match,
  history,
  onCountUpdate,
  onMessages,
}) => {
  const { user } = useContext(UserContext);
  const [rawPolysomnography, setRawPolysomnography] = useState({});
  const [polysomnography, setPolysomnography] = useState({});
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [validation, setValidation] = useState({
    customer: true,
    customerPhone: true,
    customerEmail: true,
    customerCity: true,
    customerAddress: true,
    technician: true,
    doctor: true,
    deliveryDate: true,
    pickupDate: true,
    polysomnographyStatus: true,
    recordingStatus: true,
    nextAction: true,
    manualPaymentStatus: true,
  });
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [originalCustomer, setOriginalCustomer] = useState({});

  // fetch data
  useEffect(() => {
    async function fetchData() {
      setRawPolysomnography(await getPolysomnographyById(match.params.id));
      setAllTechnicians(await getAllTechnicians());
      setAllDoctors(await getAllDoctors());
    }
    fetchData();
  }, [match.params.id]);

  // set technician options
  useEffect(() => {
    const newTechnicianOptions = allTechnicians.map((d) => ({
      id: d.id,
      value: d.id,
      label: `${d.firstName} ${d.lastName}`,
      firstName: d.firstName,
      lastName: d.lastName,
    }));

    setTechnicianOptions([BLANK, ...newTechnicianOptions]);
  }, [allTechnicians]);

  // set doctor options
  useEffect(() => {
    const newDoctorOptions = allDoctors
      .map((d) => ({
        id: d.id,
        value: d.id,
        label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        firstName: d.firstName,
        lastName: d.lastName,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));

    setDoctorOptions([BLANK, ...newDoctorOptions]);
  }, [allDoctors]);

  // set local polysomnography object
  useEffect(() => {
    setPolysomnography({
      ...rawPolysomnography,
      customer: rawPolysomnography.customer && {
        id: rawPolysomnography.customer.id,
        value: `${rawPolysomnography.customer.firstName} ${rawPolysomnography.customer.lastName}`,
        label: `${rawPolysomnography.customer.firstName} ${rawPolysomnography.customer.lastName}`,
        phone: rawPolysomnography.customer.phone,
        email: rawPolysomnography.customer.email,
        city: rawPolysomnography.customer.city,
        address: rawPolysomnography.customer.address,
      },
      status: STATUSES.find((s) => s.value === rawPolysomnography.status),
      recordingStatus: RECORDING_STATUSES.find(
        (s) => s.value === rawPolysomnography.recordingStatus,
      ),
      nextAction: NEXT_ACTIONS.find(
        (s) => s.value === rawPolysomnography.nextAction,
      ),
      manualPaymentStatus: MANUAL_PAYMENT_STATUSES.find(
        (s) => s.value === rawPolysomnography.manualPaymentStatus,
      ),
      technician: rawPolysomnography.technician
        ? technicianOptions.find(
            (d) => d.id === rawPolysomnography.technician.id,
          )
        : BLANK,
      doctor: rawPolysomnography.doctor
        ? doctorOptions.find((d) => d.id === rawPolysomnography.doctor.id)
        : BLANK,
    });
  }, [rawPolysomnography, technicianOptions, doctorOptions]);

  // save origin customer
  useEffect(() => {
    setOriginalCustomer({ ...rawPolysomnography.customer });
  }, [rawPolysomnography]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setPolysomnography({ ...polysomnography, [name]: value });
  };

  const handleCustomerChange = (e) => {
    const { customer } = polysomnography;
    const { name, value } = e.target;

    setPolysomnography({
      ...polysomnography,
      customer: { ...customer, [name]: value },
    });
  };

  const handleTechnicianSelect = (selectedOption) => {
    setPolysomnography({ ...polysomnography, technician: selectedOption });
  };

  const handleDoctorSelect = (selectedOption) => {
    setPolysomnography({ ...polysomnography, doctor: selectedOption });
    setValidation({ ...validation, doctor: !!selectedOption?.id });
  };

  const handleDateChange = ({ field, value }) =>
    setPolysomnography({ ...polysomnography, [field]: value });
  const handleSelectChange = (field, selectedOption) =>
    handleDateChange({ field, value: selectedOption });

  const isValid = () => {
    const { customer, doctor } = polysomnography;

    const newValidation = {
      customer: !!customer?.id,
      customerPhone: true,
      customerEmail: true,
      customerCity: true,
      customerAddress: true,
      technician: true,
      doctor: !!doctor?.id,
      deliveryDate: true,
      pickupDate: true,
      polysomnographyStatus: true,
      recordingStatus: true,
      nextAction: true,
      manualPaymentStatus: true,
    };

    setValidation(newValidation);
    return Object.values(newValidation).every(Boolean);
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      return;
    }

    const newPolysomnography = {
      ...polysomnography,
      status: polysomnography.status?.value,
      recordingStatus: polysomnography.recordingStatus?.value,
      nextAction: polysomnography.nextAction?.value,
      manualPaymentStatus: polysomnography.manualPaymentStatus?.value,
      customer: polysomnography.customer?.id,
    };

    const sanitizedPolysomnography =
      sanitizePolysomnography(newPolysomnography);

    const {
      meta: { messages },
    } = await updatePolysomnography(match.params.id, sanitizedPolysomnography, {
      showMessage: false,
    });

    // update customer only if it has changed
    if (
      JSON.stringify({
        phone: polysomnography.customer.phone,
        email: polysomnography.customer.email,
        city: polysomnography.customer.city,
        address: polysomnography.customer.address,
      }) !==
      JSON.stringify({
        phone: originalCustomer.phone,
        email: originalCustomer.email,
        city: originalCustomer.city,
        address: originalCustomer.address,
      })
    ) {
      await updateCustomer(polysomnography.customer.id, {
        phone: polysomnography.customer.phone,
        email: polysomnography.customer.email,
        city: polysomnography.customer.city,
        address: polysomnography.customer.address,
      });
    }

    onCountUpdate();
    onMessages(messages);

    history.goBack();
  };

  const props = {
    onChange: handleChange,
    onCustomerChange: handleCustomerChange,
    onDateChange: handleDateChange,
    onSelectChange: handleSelectChange,
    onSubmit: handleSubmit,
    onTechnicianSelect: handleTechnicianSelect,
    onDoctorSelect: handleDoctorSelect,
    polysomnography,
    technicianOptions,
    doctorOptions,
    validation,
    user,
  };

  return <ViewComponent {...props} />;
};

PolysomnographiesEdit.propTypes = {
  match: ReactRouterPropTypes.match.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default withRouter(PolysomnographiesEdit);
